// 06-snapping: candidate priority (SNP-3), screen-space tolerances (SNP-2), charged-point
// inference rays (SNP-5), ortho, Alt suspend, Tab cycle, self-close (SNP-7).
import { angleOf, dir as dirOf, dist, normalizeDeg, rotate as rotatePoint, sub, type Vec } from "../core/geometry";
import {
  arcContainsAngle,
  arcPoint,
  circleCircleIntersect,
  closestOnSeg,
  lineLineIntersect,
  segCircleIntersect,
  segSegIntersect
} from "../core/intersect";
import type { Doc, Entity } from "../doc/entities";
import type { Command } from "../doc/commands";
import type { SpatialIndex } from "./spatialIndex";
import { gridStep } from "./renderBg";

// SNP-2 — tuning values; adjust freely, structure never.
export const SNAP_TOL_PX = 10;
export const RAY_TOL_PX = 6;
export const CHARGE_TOL_PX = 8;
export const CHARGE_MS = 350;
const TAB_RESET_PX = 4;
const MAX_CHARGED = 3;
const MAX_ENTITIES = 200; // EC-SNP-1

export type SnapKind =
  | "endpoint"
  | "intersection"
  | "midpoint"
  | "center"
  | "quadrant"
  | "gridEntity"
  | "edge"
  | "grid"
  | "guide"
  | "ray";

// SNP-3 🔒 priority (lower wins).
const PRIORITY: Record<SnapKind, number> = {
  endpoint: 1,
  intersection: 2,
  midpoint: 3,
  center: 4,
  quadrant: 5,
  gridEntity: 6,
  edge: 7,
  grid: 8,
  guide: 9,
  ray: 10
};

export interface SnapRay {
  origin: Vec;
  dir: Vec; // unit
  color: "x" | "y" | "snap";
}

interface Candidate {
  p: Vec;
  kind: SnapKind;
  entityId?: string;
  d: number;
}

export interface SnapResult {
  point: Vec;
  kind: SnapKind | "free" | "ortho";
  entityId?: string;
  rays: SnapRay[];
}

export interface SnapQuery {
  cursor: Vec;
  scale: number; // px/mm — converts SNP-2 px tolerances to world
  doc: Doc;
  index: SpatialIndex;
  excludeIds?: ReadonlySet<string>;
  basePoint?: Vec | null; // tool's anchor (line start etc.) for ortho + rays
  selfClosePoint?: Vec | null; // SNP-7
  guides?: { origin: Vec; dir: Vec }[]; // ANN-7 guide lines (priority 9)
  shift: boolean;
  alt: boolean;
  now: number; // ms timestamp for charging
  /** 3.21.0: no ortho lock for this point (the opposite corner of a box: on the lock line it has no width or depth). */
  noOrtho?: boolean;
}

interface ChargedPoint {
  p: Vec;
  segDir?: Vec; // set when the point belongs to a segment → parallel/perp rays
  entityId?: string;
}

type Prim =
  | { kind: "seg"; a: Vec; b: Vec; id: string }
  | { kind: "circle"; c: Vec; r: number; id: string; arc?: { startDeg: number; sweepDeg: number } };

function primsOf(entity: Entity): Prim[] {
  switch (entity.type) {
    case "polyline": {
      const out: Prim[] = [];
      const pts = entity.points;
      for (let i = 0; i < pts.length - 1; i++) out.push({ kind: "seg", a: pts[i]!, b: pts[i + 1]!, id: entity.id });
      if (entity.closed && pts.length > 2) out.push({ kind: "seg", a: pts[pts.length - 1]!, b: pts[0]!, id: entity.id });
      return out;
    }
    case "circle":
      return [{ kind: "circle", c: entity.center, r: entity.r, id: entity.id }];
    case "arc":
      return [
        {
          kind: "circle",
          c: entity.center,
          r: entity.r,
          id: entity.id,
          arc: { startDeg: entity.startDeg, sweepDeg: entity.sweepDeg }
        }
      ];
    default:
      return [];
  }
}

function onPrim(prim: Prim, p: Vec): boolean {
  if (prim.kind === "seg") return true; // produced by seg intersection helpers already clipped
  if (!prim.arc) return true;
  return arcContainsAngle(prim.arc.startDeg, prim.arc.sweepDeg, angleOf(sub(p, prim.c)));
}

export class SnapEngine {
  enabled = true;
  orthoSticky = false; // ORTHO status-bar badge (UI-3); Shift is the hold form
  polar45 = false;
  disabledKinds = new Set<SnapKind>();

  private charged: ChargedPoint[] = [];
  private hoverKey = "";
  private hoverSince = 0;
  private tabCandidates: Candidate[] = [];
  private tabOffset = 0;
  private tabCursor: Vec = { x: 0, y: 0 };

  /** EC-SNP-4: purge charged points whose source entity changed. */
  onDocChange(cmd: Command): void {
    const touched = new Set<string>();
    const collect = (c: Command): void => {
      switch (c.kind) {
        case "addEntities":
          break;
        case "deleteEntities":
          for (const r of c.removed) touched.add(r.entity.id);
          break;
        case "updateEntities":
          for (const p of c.patches) touched.add(p.id);
          break;
        case "batch":
          c.commands.forEach(collect);
          break;
        default:
          break;
      }
    };
    collect(cmd);
    if (touched.size > 0) {
      this.charged = this.charged.filter((c) => !c.entityId || !touched.has(c.entityId));
    }
  }

  cycleTab(): void {
    if (this.tabCandidates.length > 1) this.tabOffset = (this.tabOffset + 1) % this.tabCandidates.length;
  }

  clearCharged(): void {
    this.charged = [];
  }

  resolve(q: SnapQuery): SnapResult {
    const tol = SNAP_TOL_PX / q.scale;
    const rayTol = RAY_TOL_PX / q.scale;
    const ortho = !q.noOrtho && (q.shift || this.orthoSticky);

    // Ortho lock line (independent of snapping — active even with Alt).
    let orthoLine: { origin: Vec; dir: Vec; color: "x" | "y" | "snap" } | null = null;
    if (ortho && q.basePoint) {
      const cursorAngle = angleOf(sub(q.cursor, q.basePoint));
      const step = this.polar45 ? 45 : 90;
      const locked = Math.round(cursorAngle / step) * step;
      const d = dirOf(locked);
      const color = Math.abs(d.y) < 1e-9 ? "x" : Math.abs(d.x) < 1e-9 ? "y" : "snap";
      orthoLine = { origin: q.basePoint, dir: d, color };
    }
    const projectOrtho = (p: Vec): Vec => {
      if (!orthoLine) return p;
      const t = (p.x - orthoLine.origin.x) * orthoLine.dir.x + (p.y - orthoLine.origin.y) * orthoLine.dir.y;
      return { x: orthoLine.origin.x + t * orthoLine.dir.x, y: orthoLine.origin.y + t * orthoLine.dir.y };
    };

    if (q.alt || !this.enabled) {
      // Alt suspends snapping (SNP-3); ortho constraint still applies (EC-SNP-5 area).
      const point = projectOrtho(q.cursor);
      return { point, kind: orthoLine ? "ortho" : "free", rays: orthoLine ? [orthoLine] : [] };
    }

    const { candidates, rays } = this.collect(q, tol, rayTol);
    this.updateCharging(q, candidates);

    let list = candidates.filter((c) => !this.disabledKinds.has(c.kind));
    if (orthoLine) {
      // project candidates onto the lock line; drop ones that project too far away (SNP-5)
      list = list
        .map((c) => {
          const proj = projectOrtho(c.p);
          return { ...c, p: proj, d: dist(proj, q.cursor) };
        })
        .filter((c) => dist(projectOrtho(c.p), c.p) < 1e-6 || true); // keep all; distance re-ranked
    }
    list.sort((a, b) => PRIORITY[a.kind] - PRIORITY[b.kind] || a.d - b.d);

    // Tab cycling state (SNP-3): reset when cursor moved > 4 px.
    if (dist(q.cursor, this.tabCursor) > TAB_RESET_PX / q.scale) {
      this.tabOffset = 0;
      this.tabCursor = q.cursor;
    }
    this.tabCandidates = list;

    const winner = list.length > 0 ? list[this.tabOffset % list.length]! : null;
    const activeRays: SnapRay[] = orthoLine ? [orthoLine, ...rays] : rays;

    if (winner) {
      return { point: winner.p, kind: winner.kind, entityId: winner.entityId, rays: activeRays };
    }
    const point = projectOrtho(q.cursor);
    return { point, kind: orthoLine ? "ortho" : "free", rays: activeRays };
  }

  private collect(
    q: SnapQuery,
    tol: number,
    rayTol: number
  ): { candidates: Candidate[]; rays: SnapRay[] } {
    const out: Candidate[] = [];
    const cursor = q.cursor;
    const push = (p: Vec, kind: SnapKind, entityId?: string): void => {
      const d = dist(p, cursor);
      if (d <= tol) out.push({ p, kind, entityId, d });
    };

    // SNP-7 self-close point snaps at endpoint priority.
    if (q.selfClosePoint) push(q.selfClosePoint, "endpoint");

    const box = { minX: cursor.x - tol, minY: cursor.y - tol, maxX: cursor.x + tol, maxY: cursor.y + tol };
    const layerState = new Map(q.doc.layers.map((l) => [l.id, l.visible]));
    const entities: Entity[] = [];
    for (const id of q.index.tree.query(box)) {
      if (q.excludeIds?.has(id)) continue;
      const entity = q.doc.entities[id];
      if (!entity || entity.hidden || !layerState.get(entity.layerId)) continue; // hidden never snaps; locked DOES
      entities.push(entity);
    }
    const reduced = entities.length > MAX_ENTITIES; // EC-SNP-1

    const prims: Prim[] = [];
    for (const entity of entities) {
      switch (entity.type) {
        case "polyline": {
          for (const p of entity.points) push(p, "endpoint", entity.id);
          if (!reduced || true) {
            const pts = entity.points;
            const segCount = pts.length - 1 + (entity.closed && pts.length > 2 ? 1 : 0);
            for (let i = 0; i < segCount; i++) {
              const a = pts[i]!;
              const b = pts[(i + 1) % pts.length]!;
              push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, "midpoint", entity.id);
              if (!reduced) push(closestOnSeg(cursor, a, b), "edge", entity.id);
            }
          }
          break;
        }
        case "circle": {
          push(entity.center, "center", entity.id);
          if (!reduced) {
            for (const qd of [0, 90, 180, 270]) push(arcPoint(entity.center, entity.r, qd), "quadrant", entity.id);
            const a = angleOf(sub(cursor, entity.center));
            push(arcPoint(entity.center, entity.r, a), "edge", entity.id);
          }
          break;
        }
        case "arc": {
          push(entity.center, "center", entity.id);
          push(arcPoint(entity.center, entity.r, entity.startDeg), "endpoint", entity.id);
          push(arcPoint(entity.center, entity.r, entity.startDeg + entity.sweepDeg), "endpoint", entity.id);
          push(arcPoint(entity.center, entity.r, entity.startDeg + entity.sweepDeg / 2), "midpoint", entity.id);
          if (!reduced) {
            const a = angleOf(sub(cursor, entity.center));
            if (arcContainsAngle(entity.startDeg, entity.sweepDeg, normalizeDeg(a))) {
              push(arcPoint(entity.center, entity.r, a), "edge", entity.id);
            }
          }
          break;
        }
        case "grid": {
          // ANN-6: grid-entity cell intersections snap at priority 6
          const local = Math.abs(entity.rotationDeg) > 1e-9
            ? rotatePoint(cursor, entity.pos, -entity.rotationDeg)
            : cursor;
          const gx = Math.round((local.x - entity.pos.x) / entity.cellW);
          const gy = Math.round((local.y - entity.pos.y) / entity.cellH);
          if (gx >= 0 && gx <= entity.cols && gy >= 0 && gy <= entity.rows) {
            const p = { x: entity.pos.x + gx * entity.cellW, y: entity.pos.y + gy * entity.cellH };
            push(
              Math.abs(entity.rotationDeg) > 1e-9 ? rotatePoint(p, entity.pos, entity.rotationDeg) : p,
              "gridEntity",
              entity.id
            );
          }
          break;
        }
        default:
          break;
      }
      if (!reduced) prims.push(...primsOf(entity));
    }

    // Intersections (priority 2) among nearby primitives (SNP-4).
    if (!reduced) {
      for (let i = 0; i < prims.length; i++) {
        for (let j = i + 1; j < prims.length; j++) {
          for (const p of primIntersections(prims[i]!, prims[j]!)) push(p, "intersection");
        }
      }
    }

    // Canvas grid points (priority 8) — only when grid snap is on (RND-8).
    if (q.doc.canvasGrid.snap) {
      const step = gridStep(q.scale);
      push({ x: Math.round(cursor.x / step) * step, y: Math.round(cursor.y / step) * step }, "grid");
    }

    // ANN-7 guides: project onto each guide (priority 9); guide∩guide = intersection
    const guides = q.guides ?? [];
    for (const g of guides) {
      const t = (cursor.x - g.origin.x) * g.dir.x + (cursor.y - g.origin.y) * g.dir.y;
      push({ x: g.origin.x + t * g.dir.x, y: g.origin.y + t * g.dir.y }, "guide");
    }
    for (let i = 0; i < guides.length; i++) {
      for (let j = i + 1; j < guides.length; j++) {
        const p = lineLineIntersect(guides[i]!.origin, guides[i]!.dir, guides[j]!.origin, guides[j]!.dir);
        if (p) push(p, "intersection");
      }
    }

    // Inference rays (SNP-5) from charged points and the tool base point.
    const sources: ChargedPoint[] = [...this.charged];
    if (q.basePoint) sources.push({ p: q.basePoint });
    const rays: SnapRay[] = [];
    const seen = new Set<string>();
    const addRay = (origin: Vec, d: Vec, color: SnapRay["color"]): void => {
      const ang = Math.round(normalizeDeg(angleOf(d)) % 180);
      const off = Math.round((origin.x * d.y - origin.y * d.x) * 1000) / 1000;
      const key = `${ang}:${off}`;
      if (seen.has(key)) return; // EC-SNP-6 dedupe
      seen.add(key);
      const distToLine = Math.abs((cursor.x - origin.x) * d.y - (cursor.y - origin.y) * d.x);
      if (distToLine <= rayTol) rays.push({ origin, dir: d, color });
    };
    for (const src of sources) {
      addRay(src.p, { x: 1, y: 0 }, "x");
      addRay(src.p, { x: 0, y: 1 }, "y");
      if (src.segDir) {
        addRay(src.p, src.segDir, "snap");
        addRay(src.p, { x: -src.segDir.y, y: src.segDir.x }, "snap");
      }
    }

    // Ray projections (priority 10) and ray∩ray / ray∩entity (priority 2).
    for (const ray of rays) {
      const t = (cursor.x - ray.origin.x) * ray.dir.x + (cursor.y - ray.origin.y) * ray.dir.y;
      push({ x: ray.origin.x + t * ray.dir.x, y: ray.origin.y + t * ray.dir.y }, "ray");
    }
    for (let i = 0; i < rays.length; i++) {
      for (let j = i + 1; j < rays.length; j++) {
        const p = lineLineIntersect(rays[i]!.origin, rays[i]!.dir, rays[j]!.origin, rays[j]!.dir);
        if (p) push(p, "intersection");
      }
      if (!reduced) {
        for (const prim of prims) {
          const far = 1e7;
          const a = { x: rays[i]!.origin.x - rays[i]!.dir.x * far, y: rays[i]!.origin.y - rays[i]!.dir.y * far };
          const b = { x: rays[i]!.origin.x + rays[i]!.dir.x * far, y: rays[i]!.origin.y + rays[i]!.dir.y * far };
          if (prim.kind === "seg") {
            const p = segSegIntersect(a, b, prim.a, prim.b);
            if (p) push(p, "intersection", prim.id);
          } else {
            for (const p of segCircleIntersect(a, b, prim.c, prim.r)) {
              if (onPrim(prim, p)) push(p, "intersection", prim.id);
            }
          }
        }
      }
    }

    return { candidates: out, rays };
  }

  /** SNP-5 charging: hover an endpoint/midpoint/center ≥ 350 ms within 8 px. */
  private updateCharging(q: SnapQuery, candidates: Candidate[]): void {
    const chargeTol = CHARGE_TOL_PX / q.scale;
    const target = candidates
      .filter((c) => (c.kind === "endpoint" || c.kind === "midpoint" || c.kind === "center") && c.d <= chargeTol)
      .sort((a, b) => a.d - b.d)[0];
    if (!target) {
      this.hoverKey = "";
      return;
    }
    const key = `${target.p.x.toFixed(4)}:${target.p.y.toFixed(4)}`;
    if (key !== this.hoverKey) {
      this.hoverKey = key;
      this.hoverSince = q.now;
      return;
    }
    if (q.now - this.hoverSince < CHARGE_MS) return;
    if (this.charged.some((c) => dist(c.p, target.p) < 1e-6)) return;
    let segDir: Vec | undefined;
    const entity = target.entityId ? q.doc.entities[target.entityId] : undefined;
    if (entity?.type === "polyline" && (target.kind === "endpoint" || target.kind === "midpoint")) {
      segDir = nearestSegDir(entity.points, entity.closed, target.p);
    }
    this.charged.push({ p: target.p, segDir, entityId: target.entityId });
    if (this.charged.length > MAX_CHARGED) this.charged.shift(); // FIFO
  }

  get chargedPoints(): readonly ChargedPoint[] {
    return this.charged;
  }
}

function nearestSegDir(pts: Vec[], closed: boolean, p: Vec): Vec | undefined {
  let best: { d: number; dir: Vec } | null = null;
  const segCount = pts.length - 1 + (closed && pts.length > 2 ? 1 : 0);
  for (let i = 0; i < segCount; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const d = dist(closestOnSeg(p, a, b), p);
    if (best === null || d < best.d) {
      const l = dist(a, b);
      if (l > 1e-9) best = { d, dir: { x: (b.x - a.x) / l, y: (b.y - a.y) / l } };
    }
  }
  return best?.dir;
}

function primIntersections(a: Prim, b: Prim): Vec[] {
  if (a.kind === "seg" && b.kind === "seg") {
    const p = segSegIntersect(a.a, a.b, b.a, b.b);
    return p ? [p] : [];
  }
  if (a.kind === "seg" && b.kind === "circle") {
    return segCircleIntersect(a.a, a.b, b.c, b.r).filter((p) => onPrim(b, p));
  }
  if (a.kind === "circle" && b.kind === "seg") {
    return segCircleIntersect(b.a, b.b, a.c, a.r).filter((p) => onPrim(a, p));
  }
  if (a.kind === "circle" && b.kind === "circle") {
    return circleCircleIntersect(a.c, a.r, b.c, b.r).filter((p) => onPrim(a, p) && onPrim(b, p));
  }
  return [];
}
